import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState('Main Plant');
  const [isFacilityMenuOpen, setIsFacilityMenuOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [alertCount, setAlertCount] = useState(3);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const moreMenuRef = useRef(null);
  const facilityMenuRef = useRef(null);

  const navigationItems = [
    {
      label: 'Command Center',
      path: '/operations-command-center',
      icon: 'Monitor',
      tooltip: 'Real-time operational monitoring hub'
    },
    {
      label: 'Fleet Operations',
      path: '/fleet-performance-dashboard',
      icon: 'Truck',
      tooltip: 'Vehicle tracking and route optimization'
    },
    {
      label: 'Sustainability',
      path: '/sustainability-analytics-hub',
      icon: 'Leaf',
      tooltip: 'Environmental compliance monitoring'
    },
    {
      label: '3D Container',
      path: '/',
      icon: 'Truck',
      tooltip: '3D Simulation Containers'
    },
    {
      label: 'Executive Dashboard',
      path: '/executive-summary-dashboard',
      icon: 'BarChart3',
      tooltip: 'Strategic KPIs and performance metrics'
    }
  ];

  const facilities = [
    { value: 'main-plant', label: 'Main Plant', location: 'Detroit, MI' },
    { value: 'west-facility', label: 'Manufacture A', location: 'Jakarta, ID' },
    { value: 'east-facility', label: 'Manufacture B', location: 'Surabya, ID' },
    { value: 'north-facility', label: 'Manufacture C', location: 'Semarang, ID' }
  ];

  const moreMenuItems = [
    { label: 'Settings', icon: 'Settings', path: '/settings' },
    { label: 'Help & Support', icon: 'HelpCircle', path: '/help' },
    { label: 'Admin Panel', icon: 'Shield', path: '/admin' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef?.current && !moreMenuRef?.current?.contains(event?.target)) {
        setIsMoreMenuOpen(false);
      }
      if (facilityMenuRef?.current && !facilityMenuRef?.current?.contains(event?.target)) {
        setIsFacilityMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleFacilityChange = (facility) => {
    setSelectedFacility(facility?.label);
    setIsFacilityMenuOpen(false);
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'online': return 'status-online';
      case 'warning': return 'status-warning';
      case 'offline': return 'status-offline';
      default: return 'status-online';
    }
  };

  const formatLastUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-1000 bg-card border-b border-border nav-shadow">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo Section */}
        <div className="flex items-center space-x-8">
          <div className="flex items-center space-x-3">
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground">Dashboard</span>
              <span className="text-xs text-muted-foreground font-mono">Analytics</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems?.map((item) => {
              const isActive = location?.pathname === item?.path;
              return (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`relative flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium nav-transition ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  title={item?.tooltip}
                >
                  <Icon name={item?.icon} size={16} />
                  <span>{item?.label}</span>
                  {item?.path === '/operations-command-center' && alertCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-error rounded-full">
                      {alertCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Status Indicator */}
          <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-muted rounded-lg">
            <div className={`status-indicator ${getStatusColor()}`}></div>
            <span className="text-xs font-mono text-muted-foreground">
              {connectionStatus === 'online' ? 'Live' : connectionStatus}
            </span>
            <span className="text-xs text-muted-foreground">•</span>
            <span className="text-xs text-muted-foreground">{formatLastUpdate()}</span>
          </div>

          {/* Facility Selector */}
          <div className="relative hidden md:block" ref={facilityMenuRef}>
            <button
              onClick={() => setIsFacilityMenuOpen(!isFacilityMenuOpen)}
              className="flex items-center space-x-2 px-3 py-1.5 bg-muted rounded-lg text-sm font-medium text-foreground hover:bg-border nav-transition"
            >
              <Icon name="Building2" size={16} />
              <span>{selectedFacility}</span>
              <Icon name="ChevronDown" size={14} />
            </button>

            {isFacilityMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-lg shadow-lg z-1010 animate-slide-down">
                <div className="p-2">
                  {facilities?.map((facility) => (
                    <button
                      key={facility?.value}
                      onClick={() => handleFacilityChange(facility)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm text-left rounded-md hover:bg-muted nav-transition"
                    >
                      <div>
                        <div className="font-medium text-foreground">{facility?.label}</div>
                        <div className="text-xs text-muted-foreground">{facility?.location}</div>
                      </div>
                      {selectedFacility === facility?.label && (
                        <Icon name="Check" size={16} color="var(--color-primary)" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* More Menu */}
          <div className="relative hidden lg:block" ref={moreMenuRef}>
            <button
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg nav-transition"
            >
              <span>More</span>
              <Icon name="ChevronDown" size={14} />
            </button>

            {isMoreMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg z-1010 animate-slide-down">
                <div className="p-2">
                  {moreMenuItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => {
                        handleNavigation(item?.path);
                        setIsMoreMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-left rounded-md hover:bg-muted nav-transition"
                    >
                      <Icon name={item?.icon} size={16} />
                      <span className="text-foreground">{item?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden flex items-center justify-center w-10 h-10 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg nav-transition"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </button>
        </div>
      </div>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-16 bg-background z-1020 lg:hidden animate-fade-in">
          <div className="p-6 space-y-4">
            {/* Mobile Status */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-center space-x-2">
                <div className={`status-indicator ${getStatusColor()}`}></div>
                <span className="text-sm font-medium">System Status: {connectionStatus}</span>
              </div>
              <span className="text-xs text-muted-foreground">{formatLastUpdate()}</span>
            </div>

            {/* Mobile Facility Selector */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Building2" size={16} />
                <span className="text-sm font-medium">Current Facility</span>
              </div>
              <select
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e?.target?.value)}
                className="w-full p-2 bg-background border border-border rounded-md text-sm"
              >
                {facilities?.map((facility) => (
                  <option key={facility?.value} value={facility?.label}>
                    {facility?.label} - {facility?.location}
                  </option>
                ))}
              </select>
            </div>

            {/* Mobile Navigation */}
            <nav className="space-y-2">
              {navigationItems?.map((item) => {
                const isActive = location?.pathname === item?.path;
                return (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full flex items-center space-x-3 p-4 rounded-lg text-left nav-transition ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-border'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} />
                    <div className="flex-1">
                      <div className="font-medium">{item?.label}</div>
                      <div className="text-xs opacity-75">{item?.tooltip}</div>
                    </div>
                    {item?.path === '/operations-command-center' && alertCount > 0 && (
                      <span className="flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-error rounded-full">
                        {alertCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Mobile More Menu */}
            <div className="pt-4 border-t border-border space-y-2">
              {moreMenuItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className="w-full flex items-center space-x-3 p-4 bg-muted text-foreground hover:bg-border rounded-lg nav-transition"
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="font-medium">{item?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;